<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import AccountSelector from "@/components/AccountSelector";
import NewsPreview from "@/components/NewsPreview";
import PollItem from "@/components/poll/PollItem";
import ButtonRedirect from "@/components/helpers/ButtonRedirect";
import TelegramChatVip from "@/components/TelegramChatVip.vue";
import TelegramChatNews from "@/components/TelegramChatNews.vue";
import BalanceBlock from "@/components/balance/BalanceBlock.vue";
import BalanceStatistic from "@/components/balance/BalanceStatistic.vue";
import PublicActivitiesBanner from "@/components/dashboard/PublicActivitiesBanner.vue";
import LeadershipCouncilBanner from "@/components/LeadershipCouncilBanner.vue";
import NewUsersStatistic from "@/components/dashboard/NewUsersStatistic.vue";
import AccountInfo from "@/components/dashboard/AccountInfo.vue";
import RcMarket from "@/components/dashboard/RcMarket.vue";
// import Slider from "@/components/dashboard/Slider.vue";
import MainSlider from "@/components/main-slider/MainSlider.vue";
import AgencyFees from "@/components/dashboard/AgencyFees.vue";
import { apiGetCurrentBalance } from "@/modules/agency-fees/api";
import { format, addMonths } from "date-fns";

export default {
  name: "Dashboard",

  components: {
    AgencyFees,
    // Slider,
    RcMarket,
    LeadershipCouncilBanner,
    PublicActivitiesBanner,
    ButtonRedirect,
    AccountSelector,
    NewsPreview,
    PollItem,
    // TelegramChatVip,
    // TelegramChatNews,
    BalanceBlock,
    BalanceStatistic,
    NewUsersStatistic,
    AccountInfo,
    MainSlider,
  },

  data() {
    return {
      locales: [
        {
          id: "RUB",
          name: "RU",
        },
        {
          id: "KZT",
          name: "KZ",
        },
      ],

      locale: null,
      loadingQualification: true,
      agentSubscriptions: [],
      isAgencyFeesDisabled: false,
    };
  },

  watch: {
    locale: {
      deep: true,
      handler() {
        if (this.selectedLocale.id !== this.locale.id) {
          this.setLocale(this.locale);
        }
      },
    },

    selectedBinaryAccount() {
      this.loadingQualification = true;

      this.loadQualification({
        binaryAccount: this.selectedBinaryAccount.id,
      }).finally(() => {
        this.loadingQualification = false;
      });

      this.loadTariffsOfBinaryAccount(this.selectedBinaryAccount.id);
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      selectedBinaryAccount: (state) => state.auth.selectedBinaryAccount,
      selectedLocale: (state) => state.auth.selectedLocale,
      qualification: (state) => state.qualification.qualification,
      tariffsOfBinaryAccount: (state) => state.account.tariffsOfBinaryAccount,
      polls: (state) => state.poll.polls,
      invitedTSPCount: (state) => state.qualification.invitedTSPCount,
    }),

    ...mapGetters({
      getLocaleAccounts: "auth/getLocaleAccounts",
      getUserDistributorAccount: "auth/getUserDistributorAccount",
      getUserAccounts: "auth/getUserAccounts",
      getDistributorNewsByCount: "news/getDistributorNewsByCount",
      getPublicActivitiesNewsByCount: "news/getPublicActivitiesNewsByCount",
    }),

    isShowAgency() {
      return (
        this.user?.qualification !== null &&
        this.user?.qualification?.code !== undefined &&
        this.user?.qualification?.code !== "start" &&
        !this.isAgencyFeesDisabled &&
        this.user?.representation?.country === "Россия"
      );
    },

    isShownTelegramVip() {
      return (
        this.user.tariff?.code === "vip_d" ||
        this.user.tariff?.code === "vip_gold_d"
      );
    },

    isPremiumAccount() {
      const tariffCode = this.selectedBinaryAccount?.tariff?.code ?? null;

      if (tariffCode === null) return false;

      return (
        tariffCode === "business_d" ||
        tariffCode === "base_d" ||
        tariffCode === "premium_d" ||
        tariffCode === "vip_d" ||
        tariffCode === "vip_gold_d"
      );
    },
  },

  methods: {
    ...mapMutations({
      setLocale: "auth/SET_LOCALE",
    }),

    ...mapActions({
      loadDistributorNews: "news/loadDistributorNews",
      loadPublicActivitiesNews: "news/loadPublicActivitiesNews",
      loadQualification: "qualification/loadQualification",
      loadTariffsOfBinaryAccount: "account/loadTariffsOfBinaryAccount",
      loadPolls: "poll/loadPolls",
      loadInvitedTSPCount: "qualification/loadInvitedTSPCount",
    }),

    async getData() {
      const response = await apiGetCurrentBalance();
      this.isAgencyFeesDisabled =
        response.data.data.agent_subscription_disabled;
      let subscriptions = response.data.data.agent_subscriptions.map((item) => {
        return {
          ...item,
          start_at: item.start_at ?? new Date(),
        };
      });

      if (subscriptions.length === 0) {
        const now = new Date();

        this.agentSubscriptions = Array.from({ length: 3 }, (_, i) => {
          const date = addMonths(now, i);
          return {
            headerText: format(date, "dd.MM"),
            type: "pending",
            progressFrom: "0",
            currentProgress: "0",
            progressTo: "0",
          };
        });
        return;
      }

      if (subscriptions.length < 3) {
        const lastSubscription = subscriptions[subscriptions.length - 1];

        const lastDate = lastSubscription.start_at
          ? new Date(lastSubscription.start_at)
          : new Date();

        const missing = 3 - subscriptions.length;
        const additional = Array.from({ length: missing }, (_, i) => {
          const date = addMonths(lastDate, i + 1);

          return {
            start_at: date.toISOString(),
            amount: 0,
            max_amount: 0,
            type: "pending",
          };
        });

        subscriptions = [...subscriptions, ...additional];
      }

      let foundProgress = false;

      this.agentSubscriptions = subscriptions.map((item) => {
        let type = "done";

        if (!foundProgress && item.max_amount - item.amount !== 0) {
          type = "progress";
          foundProgress = true;
        } else if (item.end_at === undefined) {
          type = "pending";
        } else if (foundProgress) {
          type = "pending";
        }

        return {
          ...item,
          headerText: format(new Date(item.start_at), "dd.MM"),
          type,
          progressFrom: "0",
          currentProgress: Math.floor(item.amount).toString(),
          progressTo: Math.floor(item.max_amount).toString(),
        };
      });
    },
  },

  async mounted() {
    if (this.selectedBinaryAccount) {
      this.loadQualification({
        binaryAccount: this.selectedBinaryAccount.id,
      }).then(() => {
        this.loadInvitedTSPCount();

        this.loadingQualification = false;
      });

      this.loadTariffsOfBinaryAccount(this.selectedBinaryAccount.id);
    }

    const promises = [
      this.loadDistributorNews({ page: 1 }),
      this.loadPublicActivitiesNews({ page: 1 }),
      this.loadPolls(),
      this.getData(),
    ];

    await Promise.all(promises);

    this.locale = this.selectedLocale;
  },
};
</script>

<template>
  <div class="dashboard">
    <!--    <Slider name="main_slider" />-->

    <MainSlider class="dashboard__slider" name="main_slider" />

    <div class="dashboard__balance balance default-content-padding">
      <div class="balance__wrapper">
        <BalanceBlock />

        <BalanceStatistic />
      </div>
    </div>

    <div
      v-if="selectedBinaryAccount || qualification"
      class="dashboard__info info"
    >
      <div v-if="selectedBinaryAccount" class="account-selector-wrapper">
        <AccountSelector />
      </div>

      <div class="info__content default-content-padding">
        <div class="info__content-wrapper">
          <NewUsersStatistic
            v-if="isPremiumAccount"
            class="info__new-users-statistic"
          />

          <div class="info__content-wrapper__blocks">
            <AgencyFees
              v-if="isShowAgency"
              :agent-subscriptions="agentSubscriptions"
            />
            <RcMarket v-if="isPremiumAccount" />
          </div>
          <AccountInfo />
        </div>
      </div>
    </div>

    <div class="dashboard__news news default-content-padding">
      <div class="dashboard__subtitle">
        <h4>Последние новости</h4>

        <ButtonRedirect
          text="Все новости"
          arrow-position="right"
          @click="$router.push({ name: 'DistributorNews' })"
        />
      </div>

      <!--      <div class="dashboard__telegram">-->
      <!--        <TelegramChatNews />-->

      <!--        <TelegramChatVip v-if="isShownTelegramVip" />-->
      <!--      </div>-->

      <div v-if="getDistributorNewsByCount(4).length > 0" class="news__list">
        <NewsPreview
          v-for="news in getDistributorNewsByCount(4)"
          :key="news.id"
          :news="news"
        />
      </div>
    </div>

    <div class="dashboard__leadership-council default-content-padding">
      <LeadershipCouncilBanner class="dashboard__leadership-council-banner" />
    </div>

    <div
      class="dashboard__public-activities public-activities default-content-padding"
    >
      <div class="dashboard__subtitle">
        <h4>Общественная деятельность</h4>

        <ButtonRedirect
          text="Все статьи"
          arrow-position="right"
          @click="$router.push({ name: 'PublicActivitiesNews' })"
        />
      </div>

      <div class="dashboard__public-activities-banner">
        <PublicActivitiesBanner />
      </div>

      <div
        v-if="getPublicActivitiesNewsByCount(4).length > 0"
        class="public-activities__list"
      >
        <NewsPreview
          v-for="news in getPublicActivitiesNewsByCount(4)"
          :key="news.id"
          :news="news"
        />
      </div>
    </div>

    <div
      v-if="polls.length > 0"
      class="dashboard__poll default-content-padding"
    >
      <div class="dashboard__subtitle">
        <h4>Опросы</h4>
      </div>

      <PollItem
        v-for="poll in polls"
        :key="poll.id"
        :poll="poll"
        @updatePolls="loadPolls"
      />
    </div>
  </div>
</template>

<style lang="scss">
.dashboard {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-bottom: $space-xxxl;

  &__slider {
    margin-bottom: 64px;

    @media (max-width: 1023px) {
      margin-bottom: 32px;
    }

    @media (max-width: 1023px) {
      margin-bottom: 24px;
    }
  }

  &__subtitle {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: $space-xl;
    max-width: 700px;

    h4 {
      @include text-1;
    }
  }

  &__balance {
    margin-bottom: 40px;
    width: 100%;

    h4 {
      margin-bottom: $space-xl;
      @include text-1;
    }
  }

  .balance {
    &__wrapper {
      display: flex;
      gap: 24px;

      @media (max-width: 1024px) {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }

      @media (max-width: 690px) {
        grid-template-columns: 1fr;
      }
    }
  }

  &__info {
    width: 100%;
    margin-bottom: 64px;

    @media (min-width: 426px) and (max-width: 576px) {
      margin-bottom: 48px;
    }
  }

  .info {
    &__content-wrapper {
      max-width: 940px;

      &__blocks {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;

        @media (min-width: 425px) {
          margin-bottom: 48px;
        }

        @media (min-width: 900px) {
          margin-bottom: 64px;
          grid-template-columns: 1fr 1fr;
        }

        @media (min-width: 1024px) {
          margin-bottom: 64px;
          grid-template-columns: 1fr;
        }

        @media (min-width: 1440px) {
          margin-bottom: 64px;
          grid-template-columns: 1fr 1fr;
        }
      }
    }

    &__content {
      background-color: $background-white;
      padding-top: 64px;
      padding-bottom: 64px;

      @media (max-width: 576px) {
        padding-top: 48px;
        padding-bottom: 48px;
      }
    }

    &__new-users-statistic {
      margin-bottom: 64px;
    }
  }

  &__founder-bonus {
    margin-bottom: $space-xxxl;
    width: 100%;

    .bonus-founder-item {
      background-color: $light-primary;
    }

    @media (min-width: 426px) and (max-width: 576px) {
      margin-bottom: $space-xxl;
    }
  }

  &__news,
  &__public-activities {
    width: 100%;
    margin-bottom: $space-xxxl;

    @media (min-width: 426px) and (max-width: 576px) {
      margin-bottom: $space-xxl;
    }
  }

  .news,
  .public-activities {
    &__list {
      max-width: 694px;
      display: grid;
      grid-template-columns: repeat(auto-fill, 335px);
      grid-gap: $space-l;
      margin-top: 32px;

      @media (min-width: 386px) and (max-width: 789px) {
        grid-template-columns: 1fr;
      }

      @media (max-width: 385px) {
        grid-template-columns: repeat(auto-fill, 100%);
      }
    }
  }

  &__leadership-council {
    margin-bottom: 64px;
    width: 100%;
  }

  &__leadership-council-banner {
    max-width: 694px;
  }

  &__telegram {
    display: grid;
    grid-template-columns: repeat(auto-fill, 335px);
    grid-gap: 24px;
    max-width: 694px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__public-activities-banner {
    max-width: 694px;
  }

  &__poll {
    margin-bottom: $space-xxxl;
    max-width: 680px;
    width: 100%;

    @media (min-width: 426px) and (max-width: 576px) {
      margin-bottom: $space-xxl;
    }
  }
}

@media (max-width: 576px) {
  .dashboard {
    .info {
      &__new-users-statistic {
        margin-bottom: 48px;
      }
    }
  }
}

@media (max-width: 425px) {
  .dashboard {
    .info {
      &__new-users-statistic {
        margin-bottom: 32px;
      }
    }
  }
}
</style>
